/*
 * Catharsis consent manager
 *
 * Shopify Customer Privacy is the sole consent authority. This controller deliberately never
 * reads a browser-stored decision to decide consent and never writes a decision except as the
 * direct result of a button click in this document.
 */
(function () {
  'use strict';

  var ROOT = '[data-consent-root]';
  var controllers = new WeakMap();
  var listenerInstalled = false;

  function text(root, key, fallback) {
    return root.getAttribute('data-consent-' + key) || fallback;
  }

  function bool(value) { return value === true; }

  function consentChoice(value) {
    if (value === true || value === 'yes') return true;
    if (value === false || value === 'no') return false;
    return null;
  }

  function consentError(result) {
    if (!result || typeof result !== 'object') return result instanceof Error;
    return Object.prototype.hasOwnProperty.call(result, 'error') || result.name === 'Error' || result.type === 'error';
  }

  function featureLoadError(result) {
    /* Shopify documents an empty object as a successful callback result. */
    if (!result) return false;
    if (typeof result !== 'object') return true;
    return consentError(result) || Object.keys(result).length > 0;
  }

  function allowedState(api) {
    if (!api) return null;
    try {
      return {
        analytics: bool(api.analyticsProcessingAllowed && api.analyticsProcessingAllowed()),
        marketing: bool(api.marketingAllowed && api.marketingAllowed()),
        preferences: bool(api.preferencesProcessingAllowed && api.preferencesProcessingAllowed())
      };
    } catch (_) {
      return null;
    }
  }

  function decisionState(api) {
    if (!api || typeof api.currentVisitorConsent !== 'function') return null;
    try {
      var consent = api.currentVisitorConsent();
      if (!consent || typeof consent !== 'object') return null;
      var keys = ['analytics', 'marketing', 'preferences'];
      if (!keys.some(function (key) { return consentChoice(consent[key]) !== null; })) return null;
      return {
        complete: keys.every(function (key) { return consentChoice(consent[key]) !== null; }),
        analytics: consentChoice(consent.analytics) === true,
        marketing: consentChoice(consent.marketing) === true,
        preferences: consentChoice(consent.preferences) === true
      };
    } catch (_) {
      return null;
    }
  }

  function updateGoogle(state) {
    if (!state || typeof window.gtag !== 'function') return;
    window.gtag('consent', 'update', {
      analytics_storage: state.analytics ? 'granted' : 'denied',
      ad_storage: state.marketing ? 'granted' : 'denied',
      ad_user_data: state.marketing ? 'granted' : 'denied',
      ad_personalization: state.marketing ? 'granted' : 'denied',
      functionality_storage: state.preferences ? 'granted' : 'denied',
      personalization_storage: state.preferences ? 'granted' : 'denied',
      security_storage: 'granted'
    });
  }

  function apiForPage() {
    return window.Shopify && window.Shopify.customerPrivacy;
  }

  function setup(root) {
    if (!root || controllers.has(root)) return controllers.get(root);

    var preview = root.hasAttribute('data-consent-preview');
    var awaitingFreshClick = false;
    var prefs = root.querySelector('[data-consent-prefs]');
    var statusWrap = root.querySelector('.consent__status-wrap');
    var status = root.querySelector('[data-consent-status]');
    var retry = root.querySelector('[data-consent-retry]');
    var lastFocus = null;
    var openedFromReopen = false;
    var ready = false;
    var loading = false;
    var submitting = false;
    var loadToken = 0;
    var timeout = null;
    var submitToken = 0;
    var submitTimeout = null;
    var destroyed = false;

    function buttons() {
      return Array.prototype.slice.call(root.querySelectorAll('button, input[type="checkbox"]'));
    }

    function setStatus(message, isError) {
      if (!status) return;
      status.textContent = message || '';
      status.toggleAttribute('data-consent-status-error', Boolean(isError));
    }

    function setBusy(value) {
      submitting = value;
      buttons().forEach(function (control) { control.disabled = value; });
      if (retry) retry.disabled = value;
    }

    function show() {
      root.hidden = false;
      root.setAttribute('data-visible', '');
      root.removeAttribute('data-immediate');
    }

    function hide() {
      root.removeAttribute('data-visible');
      root.hidden = true;
    }

    function selections() {
      var values = {};
      ['analytics', 'marketing', 'preferences'].forEach(function (purpose) {
        var input = root.querySelector('[data-consent-purpose="' + purpose + '"]');
        values[purpose] = Boolean(input && input.checked);
      });
      return values;
    }

    function renderSelections(values) {
      if (!values) return;
      ['analytics', 'marketing', 'preferences'].forEach(function (purpose) {
        var input = root.querySelector('[data-consent-purpose="' + purpose + '"]');
        if (input) input.checked = Boolean(values[purpose]);
      });
    }

    function focusables() {
      if (!prefs) return [];
      return Array.prototype.slice.call(prefs.querySelectorAll('button, input, [href], select, textarea, [tabindex]:not([tabindex="-1"])'))
        .filter(function (element) { return !element.disabled && element.offsetParent !== null; });
    }

    function closePrefs() {
      if (!prefs || prefs.hidden) return;
      prefs.hidden = true;
      if (statusWrap) root.appendChild(statusWrap);
      root.removeAttribute('data-consent-modal-open');
      if (openedFromReopen) hide();
      if (lastFocus && document.contains(lastFocus)) lastFocus.focus({ preventScroll: true });
      lastFocus = null;
      openedFromReopen = false;
    }

    function openPrefs(trigger) {
      show();
      lastFocus = trigger || document.activeElement;
      openedFromReopen = Boolean(trigger && typeof trigger.matches === 'function' && trigger.matches('[data-consent-reopen]'));
      var live = preview ? null : decisionState(apiForPage());
      renderSelections(live || selections());
      if (!prefs) return;
      if (statusWrap) prefs.querySelector('.consent__prefs-inner').appendChild(statusWrap);
      prefs.hidden = false;
      root.setAttribute('data-consent-modal-open', '');
      var first = focusables()[0];
      if (first) first.focus({ preventScroll: true });
    }

    function trapFocus(event) {
      if (!prefs || prefs.hidden || event.key !== 'Tab') return;
      var items = focusables();
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }

    function confirmSuccess() {
      setBusy(false);
      var api = apiForPage();
      updateGoogle(allowedState(api));
      closePrefs();
      hide();
      var reopen = document.querySelector('[data-consent-reopen]');
      if (reopen && root.contains(document.activeElement)) reopen.focus({ preventScroll: true });
    }

    function clearSubmitTimeout() {
      if (!submitTimeout) return;
      window.clearTimeout(submitTimeout);
      submitTimeout = null;
    }

    function submissionFailed() {
      awaitingFreshClick = true;
      setBusy(false);
      show();
      setStatus(text(root, 'error', 'Vos choix n’ont pas pu être enregistrés.'), true);
      if (retry) retry.hidden = false;
    }

    function submitFromClick(choice) {
      if (preview) { setStatus(text(root, 'preview-message', 'Aperçu : vos choix ne sont pas enregistrés.')); return; }
      var api = apiForPage();
      if (!ready || !api || typeof api.setTrackingConsent !== 'function') {
        show();
        if (loading) setStatus(text(root, 'loading', 'Chargement de vos préférences…'));
        else {
          setStatus(text(root, 'unavailable', 'Le service de confidentialité est indisponible.'), true);
          if (retry) retry.hidden = false;
        }
        return;
      }
      if (submitting) return;
      awaitingFreshClick = false;
      setBusy(true);
      if (retry) retry.hidden = true;
      setStatus(text(root, 'saving', 'Enregistrement de vos choix…'));
      var token = ++submitToken;
      clearSubmitTimeout();
      submitTimeout = window.setTimeout(function () {
        if (destroyed || token !== submitToken) return;
        submitToken += 1;
        submitTimeout = null;
        submissionFailed();
      }, 8000);
      try {
        api.setTrackingConsent({
          analytics: Boolean(choice.analytics),
          marketing: Boolean(choice.marketing),
          preferences: Boolean(choice.preferences)
        }, function (error) {
          if (destroyed || token !== submitToken) return;
          clearSubmitTimeout();
          if (consentError(error)) {
            submitToken += 1;
            submissionFailed();
            return;
          }
          submitToken += 1;
          confirmSuccess();
        });
      } catch (_) {
        clearSubmitTimeout();
        submitToken += 1;
        submissionFailed();
      }
    }

    function reconcile() {
      if (preview) { show(); return; }
      var api = apiForPage();
      var allowed = allowedState(api);
      updateGoogle(allowed);
      var decision = decisionState(api);
      renderSelections(decision);
      if (awaitingFreshClick || (prefs && !prefs.hidden)) { show(); return; }
      // Shopify's banner flag describes regional eligibility and can remain true after a
      // saved choice. Read the explicit decision separately; processing still uses Allowed.
      if (decision && decision.complete) { hide(); return; }
      try {
        if (api && typeof api.shouldShowBanner === 'function') {
          if (api.shouldShowBanner()) show();
          else hide();
          return;
        }
      } catch (_) {}
      show();
    }

    function loadPrivacyFeature() {
      if (destroyed) return;
      if (preview) { show(); setStatus(text(root, 'preview-message', 'Aperçu : vos choix ne sont pas enregistrés.')); return; }
      var shopify = window.Shopify;
      if (!shopify || typeof shopify.loadFeatures !== 'function') {
        ready = false;
        loading = false;
        show();
        setStatus(text(root, 'unavailable', 'Le service de confidentialité est indisponible.'));
        if (retry) retry.hidden = false;
        return;
      }
      if (timeout) window.clearTimeout(timeout);
      var token = ++loadToken;
      ready = false;
      loading = true;
      setStatus(text(root, 'loading', 'Chargement de vos préférences…'));
      if (retry) retry.hidden = true;
      timeout = window.setTimeout(function () {
        if (destroyed || token !== loadToken || ready) return;
        loadToken += 1;
        timeout = null;
        ready = false;
        loading = false;
        show();
        setStatus(text(root, 'unavailable', 'Le service de confidentialité est indisponible.'), true);
        if (retry) retry.hidden = false;
      }, 8000);
      try {
        shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
          if (destroyed || token !== loadToken) return;
          if (timeout) window.clearTimeout(timeout);
          timeout = null;
          loading = false;
          if (featureLoadError(error) || !apiForPage()) {
            ready = false;
            show();
            setStatus(text(root, 'unavailable', 'Le service de confidentialité est indisponible.'), true);
            if (retry) retry.hidden = false;
            return;
          }
          ready = true;
          setStatus('');
          reconcile();
        });
      } catch (_) {
        if (timeout) window.clearTimeout(timeout);
        timeout = null;
        ready = false;
        loading = false;
        show();
        setStatus(text(root, 'unavailable', 'Le service de confidentialité est indisponible.'), true);
        if (retry) retry.hidden = false;
      }
    }

    function onConsentEvent() {
      if (preview) return;
      var api = apiForPage();
      updateGoogle(allowedState(api));
      renderSelections(decisionState(api));
      /* The callback is the success boundary for choices made in this UI. */
      if (!submitting && ready) reconcile();
    }

    function onKeydown(event) {
      trapFocus(event);
      if (event.key === 'Escape' && prefs && !prefs.hidden) closePrefs();
    }

    function onRootClick(event) {
      var target = event.target.closest('button');
      if (!target || !root.contains(target)) return;
      if (target.matches('[data-consent-accept-all]')) submitFromClick({ analytics: true, marketing: true, preferences: true });
      else if (target.matches('[data-consent-reject-all]')) submitFromClick({ analytics: false, marketing: false, preferences: false });
      else if (target.matches('[data-consent-customize]')) openPrefs(target);
      else if (target.matches('[data-consent-prefs-close]')) closePrefs();
      else if (target.matches('[data-consent-save]')) submitFromClick(selections());
      else if (target.matches('[data-consent-retry]')) loadPrivacyFeature();
    }

    root.addEventListener('click', onRootClick);
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('visitorConsentCollected', onConsentEvent);

    var controller = {
      open: function (trigger) { openPrefs(trigger); },
      retry: loadPrivacyFeature,
      reconcile: reconcile,
      destroy: function () {
        destroyed = true;
        if (timeout) window.clearTimeout(timeout);
        clearSubmitTimeout();
        root.removeEventListener('click', onRootClick);
        document.removeEventListener('keydown', onKeydown);
        document.removeEventListener('visitorConsentCollected', onConsentEvent);
        controllers.delete(root);
      }
    };
    controllers.set(root, controller);
    show();
    loadPrivacyFeature();
    return controller;
  }

  function initialise(scope) {
    if (!scope) return;
    if (scope.matches && scope.matches(ROOT)) setup(scope);
    if (scope.querySelectorAll) scope.querySelectorAll(ROOT).forEach(setup);
  }

  function installDocumentListeners() {
    if (listenerInstalled) return;
    listenerInstalled = true;
    document.addEventListener('click', function (event) {
      var reopen = event.target.closest('[data-consent-reopen]');
      if (!reopen) return;
      var root = document.querySelector(ROOT);
      var controller = setup(root);
      if (controller) controller.open(reopen);
    });
    document.addEventListener('shopify:section:load', function (event) { initialise(event.target); });
    document.addEventListener('shopify:section:unload', function (event) {
      var root = event.target && (event.target.matches && event.target.matches(ROOT) ? event.target : event.target.querySelector && event.target.querySelector(ROOT));
      var controller = root && controllers.get(root);
      if (controller) controller.destroy();
    });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState !== 'visible') return;
      var root = document.querySelector(ROOT);
      var controller = root && controllers.get(root);
      if (controller) controller.reconcile();
    });
  }

  function boot() {
    installDocumentListeners();
    initialise(document);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
